import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from './AnalyticsTracker';
import Navigation from './Navigation';
import { useTrial } from '../hooks/useTrial';
import TrialBanner from './billing/TrialBanner';
import TrialExpiredModal from './billing/TrialExpiredModal';

// Eagerly loaded (landing page is the entry point)
import LandingPage from './LandingPage';

// Lazy-loaded route components for code splitting
const ExchangeFlow = lazy(() => import('./ExchangeFlow').then(m => ({ default: m.ExchangeFlow })));
const OnboardingFlow = lazy(() => import('./OnboardingFlow').then(m => ({ default: m.OnboardingFlow })));
const PricingPage = lazy(() => import('../pages/PricingPage'));
const PosterPage = lazy(() => import('../pages/PosterPage'));
const CardBackPage = lazy(() => import('../pages/CardBackPage').then(m => ({ default: m.CardBackPage })));
const VerifyEmailPage = lazy(() => import('../pages/VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const CardCreation = lazy(() => import('../pages/CardCreation'));
const Onboarding = lazy(() => import('../pages/Onboarding'));
const Gifts = lazy(() => import('../pages/Gifts'));
const AnalyticsPage = lazy(() => import('../pages/Analytics'));
const NotFound = lazy(() => import('../pages/NotFound'));
const DashboardPage = lazy(() => import('../pages/dashboard'));
const TeamDashboard = lazy(() => import('../pages/dashboard/team'));
const CardsPage = lazy(() => import('../pages/dashboard/cards'));
const NotificationSettingsPage = lazy(() => import('../pages/NotificationSettings'));
const ContactsDashboard = lazy(() => import('../pages/dashboard/contacts'));

const CardBuilderPage = lazy(() => import('../pages/onboarding/card-builder'));
const SharePage = lazy(() => import('../pages/share/[slug]'));
const EmbedPage = lazy(() => import('../pages/embed'));

// Solution landing pages
const NFCBusinessCardsForTeams = lazy(() => import('../pages/solutions/nfc-business-cards-for-teams'));
const QRCodeBusinessCardGenerator = lazy(() => import('../pages/solutions/qr-code-business-card-generator'));
const DigitalBusinessCardCRMIntegration = lazy(() => import('../pages/solutions/digital-business-card-crm-integration'));
const FreeDigitalBusinessCard = lazy(() => import('../pages/solutions/free-digital-business-card'));
const BusinessCardAPIDevelopers = lazy(() => import('../pages/solutions/business-card-api-developers'));
const HowItWorks = lazy(() => import('../pages/solutions/how-it-works'));
const DeploymentPage = lazy(() => import('../pages/solutions/deployment'));
const PrivacyPolicy = lazy(() => import('./legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./legal/TermsOfService').then(m => ({ default: m.TermsOfService })));
const CookiePolicy = lazy(() => import('./legal/CookiePolicy').then(m => ({ default: m.CookiePolicy })));

// Loading fallback component
const RouteLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[60vh] text-dark-text-secondary font-sans text-sm">
    <div className="text-center">
      <div className="w-8 h-8 border-3 border-dark-border border-t-blue-500 rounded-full animate-spin mx-auto mb-3" />
      Loading...
    </div>
  </div>
);

export const AppRouter: React.FC = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const { userPlan } = useTrial();
  const location = useLocation();
  const navigate = useNavigate();

  // Show expired modal once when trial expires
  useEffect(() => {
    if (userPlan?.trialStatus?.hasExpired && user) {
      const dismissed = sessionStorage.getItem('trial_expired_dismissed');
      if (!dismissed) setShowExpiredModal(true);
    }
  }, [userPlan, user]);

  useEffect(() => {
    // Check if user is returning or has specific URL parameters
    const urlParams = new URLSearchParams(location.search);
    const referralCode = urlParams.get('ref');

    if (referralCode) {
      // Store referral code for registration
      localStorage.setItem('referralCode', referralCode);
      analytics.track('referral_landing', { referralCode });
    }

    // If user is not logged in and not on a public page, redirect to landing
    const publicPaths = [
      '/privacy-policy',
      '/terms-of-service',
      '/cookie-policy',
      '/legal/terms',
      '/legal/privacy',
      '/legal/cookies',
      '/pricing',
      '/verify-email',
      '/reset-password',
      '/subscription/success',
      '/subscription/cancel',
      '/solutions/nfc-business-cards-for-teams',
      '/solutions/qr-code-business-card-generator',
      '/solutions/digital-business-card-crm-integration',
      '/solutions/free-digital-business-card',
      '/solutions/business-card-api-developers',
      '/solutions/how-it-works',
      '/solutions/deployment',
    ];
    // Wait for the stored session to be checked before deciding the user is logged out,
    // otherwise a page refresh on any protected page bounces to the landing page.
    if (authLoading) return;
    const sharePaths =
      location.pathname.startsWith('/share/') ||
      location.pathname === '/embed' ||
      location.pathname === '/demo' ||
      (location.pathname.startsWith('/card/') && !location.pathname.endsWith('/back'));
    const authPaths = location.pathname === '/login' || location.pathname === '/register';
    if (!user && !publicPaths.includes(location.pathname) && !sharePaths && !authPaths && location.pathname !== '/') {
      navigate('/');
    }
  }, [user, authLoading, location, navigate]);

  // Check if user needs onboarding — redirect to the multi-step wizard
  useEffect(() => {
    if (user && location.pathname === '/app') {
      const hasCompletedOnboarding = localStorage.getItem('onboarding_completed');
      if (!hasCompletedOnboarding) {
        navigate('/onboarding/card-builder');
      }
    }
  }, [user, location.pathname, navigate]);

  const handleGetStarted = () => {
    navigate('/app');
    analytics.track('app_entered_from_landing');
  };

  const handleBackToLanding = () => {
    navigate('/');
    analytics.track('returned_to_landing');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // Protected route component
  const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (authLoading) {
      return <RouteLoadingFallback />;
    }
    if (!user) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  const isPosterPage = location.pathname.startsWith('/poster');
  const isLandingPage = ['/', '/login', '/register'].includes(location.pathname);
  // Public card pages are what recipients see: no app navigation or trial banners.
  const isPublicCardPage =
    location.pathname.startsWith('/share/') ||
    (location.pathname.startsWith('/card/') && !location.pathname.endsWith('/back'));

  return (
    <div className={`app ${isLandingPage ? '' : 'app-dark'} relative min-h-screen`}>
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      {user && userPlan?.trialStatus && (userPlan.trialStatus.isActive || userPlan.trialStatus.hasExpired) && !isLandingPage && !isPublicCardPage && (
        <TrialBanner trialStatus={userPlan.trialStatus} />
      )}
      {!isPosterPage && !isLandingPage && !isPublicCardPage && <Navigation />}

      {showExpiredModal && (
        <TrialExpiredModal onClose={() => {
          setShowExpiredModal(false);
          sessionStorage.setItem('trial_expired_dismissed', '1');
        }} />
      )}

      <main id="main-content" role="main">
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage onGetStarted={handleGetStarted} />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                {showOnboarding ? (
                  <OnboardingFlow onComplete={() => setShowOnboarding(false)} />
                ) : (
                  <ExchangeFlow />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/poster"
            element={
              <ProtectedRoute>
                <PosterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/card/:cardId/back"
            element={
              <ProtectedRoute>
                <CardBackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/poster/:cardId"
            element={
              <ProtectedRoute>
                <PosterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/card-builder"
            element={
              <ProtectedRoute>
                <CardBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/card-creation"
            element={
              <ProtectedRoute>
                <CardCreation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-card"
            element={
              <ProtectedRoute>
                <CardCreation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gifts"
            element={
              <ProtectedRoute>
                <Gifts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <TeamDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cards"
            element={
              <ProtectedRoute>
                <CardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/contacts"
            element={
              <ProtectedRoute>
                <ContactsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/subscription/success" element={<PricingPage />} />
          <Route path="/subscription/cancel" element={<PricingPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy onBack={handleBackToHome} />} />
          <Route path="/terms-of-service" element={<TermsOfService onBack={handleBackToHome} />} />
          <Route path="/cookie-policy" element={<CookiePolicy onBack={handleBackToHome} />} />

          {/* Legal routes (alternative paths) */}
          <Route path="/legal/terms" element={<TermsOfService onBack={handleBackToHome} />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy onBack={handleBackToHome} />} />
          <Route path="/legal/cookies" element={<CookiePolicy onBack={handleBackToHome} />} />

          {/* Auth routes: open the sign-in / sign-up dialog on the landing page */}
          <Route
            path="/login"
            element={user ? <Navigate to="/app" replace /> : <LandingPage onGetStarted={handleGetStarted} initialAuthMode="login" />}
          />
          <Route
            path="/register"
            element={user ? <Navigate to="/app" replace /> : <LandingPage onGetStarted={handleGetStarted} initialAuthMode="register" />}
          />
          <Route path="/signup" element={<Navigate to="/register" replace />} />

          {/* Solution landing pages */}
          <Route path="/solutions/nfc-business-cards-for-teams" element={<NFCBusinessCardsForTeams />} />
          <Route path="/solutions/qr-code-business-card-generator" element={<QRCodeBusinessCardGenerator />} />
          <Route path="/solutions/digital-business-card-crm-integration" element={<DigitalBusinessCardCRMIntegration />} />
          <Route path="/solutions/free-digital-business-card" element={<FreeDigitalBusinessCard />} />
          <Route path="/solutions/business-card-api-developers" element={<BusinessCardAPIDevelopers />} />
          <Route path="/solutions/how-it-works" element={<HowItWorks />} />
          <Route path="/solutions/deployment" element={<DeploymentPage />} />

          {/* Sharing routes (public) */}
          <Route path="/share/:slug" element={<SharePage />} />
          <Route path="/card/:cardId" element={<SharePage />} />
          <Route path="/demo" element={<Navigate to="/card/demo" replace />} />
          <Route path="/embed" element={<EmbedPage />} />

          {/* 404 catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </main>
    </div>
  );
};

export default AppRouter;
